(() => {
  if (window.galixHeroWorlds) return;

  function init(hero) {
    if (hero.heroWorldsController) return;

    const scenes = [...hero.querySelectorAll('[data-hero-scene]')];
    const lamp = hero.querySelector('[data-lamp-trigger]');
    const status = hero.querySelector('[data-hero-world-status]');
    if (scenes.length < 2 || !lamp) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const prepared = new WeakMap();
    const namesByLanguage = {
      es: { galaxy: 'Galaxia', moon: 'Luna', aurora: 'Auroras', jupiter: 'Júpiter', ocean: 'Océano', pillars: 'Pilares de la Creación' },
      en: { galaxy: 'Galaxy', moon: 'Moon', aurora: 'Aurora', jupiter: 'Jupiter', ocean: 'Ocean', pillars: 'Pillars of Creation' },
      de: { galaxy: 'Galaxie', moon: 'Mond', aurora: 'Polarlichter', jupiter: 'Jupiter', ocean: 'Ozean', pillars: 'Säulen der Schöpfung' }
    };
    const language = (document.documentElement.lang || 'es').slice(0, 2).toLowerCase();
    const names = namesByLanguage[language] || namesByLanguage.es;
    const projectionLabel = ({ es: 'Proyección:', en: 'Projection:', de: 'Projektion:' })[language] || 'Proyección:';
    let active = Math.max(0, scenes.findIndex((scene) => scene.classList.contains('is-active')));
    let pendingSteps = 0;
    let transitioning = false;
    let finishTimer;
    hero.heroWorldsController = controller;

    function prepare(scene) {
      if (prepared.has(scene)) return prepared.get(scene);
      const source = scene.dataset.src;
      if (source) {
        scene.src = source;
        delete scene.dataset.src;
      }
      const ready = new Promise((resolve) => {
        if (scene.complete) {
          resolve(scene.naturalWidth > 0);
          return;
        }
        scene.addEventListener('load', () => resolve(true), { once: true, signal });
        scene.addEventListener('error', () => resolve(false), { once: true, signal });
      });
      prepared.set(scene, ready);
      return ready;
    }

    function positionLamp() {
      const image = scenes[0];
      const box = image.getBoundingClientRect();
      const root = hero.getBoundingClientRect();
      const style = getComputedStyle(image);
      const sourceWidth = image.naturalWidth || Number(image.getAttribute('width'));
      const sourceHeight = image.naturalHeight || Number(image.getAttribute('height'));
      if (!box.width || !box.height || !sourceWidth || !sourceHeight) return;

      let drawnWidth = box.width;
      let drawnHeight = box.height;
      let offsetX = 0;
      let offsetY = 0;
      if (style.objectFit === 'cover' || style.objectFit === 'contain') {
        const scale = style.objectFit === 'cover'
          ? Math.max(box.width / sourceWidth, box.height / sourceHeight)
          : Math.min(box.width / sourceWidth, box.height / sourceHeight);
        drawnWidth = sourceWidth * scale;
        drawnHeight = sourceHeight * scale;
        const [x = '50%', y = '50%'] = style.objectPosition.split(/\s+/);
        offsetX = (box.width - drawnWidth) * (parseFloat(x) / 100);
        offsetY = (box.height - drawnHeight) * (parseFloat(y) / 100);
      }

      const hitX = box.left - root.left + offsetX + drawnWidth * .765;
      const hitY = box.top - root.top + offsetY + drawnHeight * .75;
      const hitWidth = drawnWidth * .065;
      const hitHeight = drawnHeight * .145;
      hero.style.setProperty('--lamp-hit-x', `${hitX}px`);
      hero.style.setProperty('--lamp-hit-y', `${hitY}px`);
      hero.style.setProperty('--lamp-hit-width', `${hitWidth}px`);
      hero.style.setProperty('--lamp-hit-height', `${hitHeight}px`);
      hero.dataset.lampReady = '';
    }

    async function showNext() {
      if (signal.aborted || transitioning || pendingSteps === 0) return;
      transitioning = true;
      const next = (active + 1) % scenes.length;
      const current = scenes[next];
      const loaded = await prepare(current);
      if (signal.aborted) return;
      if (!loaded) {
        pendingSteps = 0;
        transitioning = false;
        return;
      }

      pendingSteps--;
      clearTimeout(finishTimer);
      const previous = scenes[active];
      active = next;
      scenes.forEach((scene) => scene.classList.remove('is-exiting', 'is-entering'));
      previous.classList.add('is-exiting');
      previous.classList.remove('is-active');
      current.classList.add('is-active', 'is-entering');
      hero.dataset.activeHeroWorld = current.dataset.heroScene;
      if (status) status.textContent = `${projectionLabel} ${names[current.dataset.heroScene] || current.dataset.heroScene}`;
      prepare(scenes[(active + 1) % scenes.length]);

      const finish = () => {
        previous.classList.remove('is-exiting');
        current.classList.remove('is-entering');
        transitioning = false;
        showNext();
      };
      if (reducedMotion.matches) finish();
      else finishTimer = setTimeout(finish, 1000);
    }

    function advance() {
      if (signal.aborted) return;
      pendingSteps = Math.min(pendingSteps + 1, scenes.length);
      showNext();
    }

    lamp.addEventListener('click', () => {
      hero.dataset.heroInteracted = '';
      advance();
    }, { signal });

    const resizeObserver = new ResizeObserver(positionLamp);
    resizeObserver.observe(hero);
    window.addEventListener('resize', positionLamp, { passive: true, signal });
    positionLamp();
    prepare(scenes[(active + 1) % scenes.length]);
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 500));
    idle(() => {
      if (!signal.aborted) scenes.forEach(prepare);
    });

    signal.addEventListener('abort', () => {
      clearTimeout(finishTimer);
      resizeObserver.disconnect();
      delete hero.dataset.lampReady;
      delete hero.heroWorldsController;
    }, { once: true });
  }

  function mount(root = document) {
    if (root.matches?.('[data-hero-worlds]')) init(root);
    root.querySelectorAll('[data-hero-worlds]').forEach(init);
  }

  window.galixHeroWorlds = { mount };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-hero-worlds]')) event.target.heroWorldsController?.abort();
    event.target.querySelectorAll('[data-hero-worlds]').forEach((hero) => hero.heroWorldsController?.abort());
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
})();
